import * as React  from "react";

import "./tinyMceField.scss";

import {Editor}         from "@Common/libs/tinymce/tinymce";
import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {Load2}          from "@Common/react/load2";
import {cx}             from "emotion";
import {Static2}        from "@Common/react/static-2";
import {UseState}       from "@Common/react/use-state";
import {ButtonSpinner}  from "@Common/ui-components/loading-spinners/button-spinner";
import {FieldOptions}   from "@Common/ui-components/field-options/fieldOptions";


export const TinyMceField = ({idOrder, fieldId, fieldValue, setFieldValue, fieldData, editorRef, aiTextGenerating, setAiTextGenerating, companyPrefs, replaceStr}) => cs(
    consumeContext("session"),
    consumeContext("propertyWizardInsertValues"),
    ["fieldValueFromWizard", ({propertyWizardInsertValues}, next) => next(propertyWizardInsertValues[fieldData.idPrimary] ?? null)],
    ["tabGuardStatic", ({session}, next) => Static2({
        next,
        getInitValue: () => ({
            iframeWindow: null,
            onBlur: () => session.tabGuard.onTabBlur?.("tinymce"),
            onFocus: () => session.tabGuard.onTabFocus?.("tinymce"),
        }),
    })],
    ["spellCheckEnabled", ({}, next) => UseState({ initValue: false, next })],  // State for Spell Check Toggle
    ["enableAiTools", ({}, next) => next(companyPrefs.suggestedCommentsMode !== 0)],
    ["showCharCountModal", ({}, next) => UseState({
        initValue: false,
        next
    })],
    ["overrideCKValue", ({}, next) => UseState({
        initValue: (fieldData.maxLength - replaceStr(fieldValue).length) < 0 ? true : false,
        next
    })],
    ["modalMessage", ({}, next) => UseState({
        initValue: "",
        next
    })],
    ["okCallback", ({}, next) => UseState({
        initValue: () => {null},
        next
    })],
    ["cancelCallback", ({}, next) => UseState({
        initValue: () => {null},
        next
    })],
    ["modalCancelButton", ({}, next) => UseState({
        initValue: true,
        next
    })],
    ["editorValue", ({}, next) => UseState({
        initValue: fieldValue ? fieldValue : "<p></p>",
        next
    })],
    ["overrideCheckHandler", ({showCharCountModal, overrideCKValue, modalMessage, okCallback, modalCancelButton, cancelCallback, editorValue}, next) => next( () => {
        if (!overrideCKValue.value) {
            modalMessage.onChange('If you would like to OVERRIDE the character counter and add additional text, please click OK.\r\n\r\nYour proof will display EXACTLY how your text will appear.\r\n\r\nNO CREDITS WILL BE GIVEN TOWARDS ORDERS THAT WERE PLACED WITH ADDITIONAL TEXT.\r\n');
            okCallback.onChange(() => {
                overrideCKValue.onChange(true);
                showCharCountModal.onChange(false);
            });
            cancelCallback.onChange(() => {
                showCharCountModal.onChange(false);
            });
            modalCancelButton.onChange(true);
            return showCharCountModal.onChange(true);
        } else {
            let find = [
                '<p>',
                '</p>',
                '\\i',
                '\\i',
                '\\b',
                '\\b',
                '\\u',
                '\\u',
            ];

            function findReplace(find, string) {
                for (let i = 0; i < find.length; i++) {
                    string = string.replaceAll(find[i], "");
                }
                return string;
            }

            function removeLastCharsInsideTag(inputString, tagName, numCharsToRemove) {
                const regex = new RegExp(`(<${tagName}>[^<]*)[^<]{${numCharsToRemove}}(<\/${tagName}>)`, 'i');
                return inputString.replace(regex, '$1$2');
            }

            let stringVal = findReplace(find, fieldValue);

            if (stringVal.length > fieldData.maxLength) {
                modalMessage.onChange(`Override will be deactivated. Text in field "${fieldData.displayLabel}" will be set to last auto saved state`);
                okCallback.onChange(() => {
                    overrideCKValue.onChange(false);
                    editorRef.current[fieldId].undoManager.undo();
                    // Create a temporary element to parse the HTML
                    let tempElement = document.createElement('div');
                    tempElement.innerHTML = fieldValue;
                    // Extract the text content
                    let textContent = tempElement.textContent;
                    // Get the character count
                    let characterCount = textContent.length;

                    editorValue.onChange(removeLastCharsInsideTag(fieldValue, 'p', characterCount-fieldData.maxLength));
                    showCharCountModal.onChange(false);
                });
                cancelCallback.onChange(() => {
                    showCharCountModal.onChange(false);
                });
                modalCancelButton.onChange(true);
                return showCharCountModal.onChange(true);
            } else {
                overrideCKValue.onChange(!overrideCKValue.value);
            }
        }
    })],
    ["showFieldOptions", ({}, next) => UseState({
        initValue: false,
        next
    })],
    ["showAiIcon", ({enableAiTools}, next) => UseState({
        initValue: enableAiTools,
        next
    })],
    ["isRefresh", ({}, next) => {
        // Initialize the value based on sessionStorage check
        const wasRefreshed = sessionStorage.getItem('tinymce_loaded') ? true : false;

        // Set the flag for future loads
        if (!wasRefreshed) {
            sessionStorage.setItem('tinymce_loaded', 'true');
        }

        return UseState({
            initValue: wasRefreshed,
            next
        });
    }],
    (({fieldValueFromWizard}, next) => Load2({
        _key: fieldValueFromWizard,
        fetch: async() => {
            if (fieldValueFromWizard) {
                setFieldValue(fieldValueFromWizard);
                if (editorRef.current[fieldId]?.initialized) {
                    editorRef.current[fieldId].setContent(fieldValueFromWizard);
                }
            }
        },
        next
    })),
    ({enableAiTools, showCharCountModal, overrideCKValue, overrideCheckHandler, modalMessage, okCallback, modalCancelButton, cancelCallback, editorValue, showFieldOptions, showAiIcon, tabGuardStatic, spellCheckEnabled, isRefresh}) => (
        <div className={showFieldOptions.value ? "tiny-mce-field-47p" : "tiny-mce-field-47p"} id={`tiny-mce-field-${fieldId}`}>
            <FieldOptions
                reference={{
                    get: () => editorRef.current[fieldId],
                }}
                fieldId={fieldId}
                idOrder={idOrder}
                fieldData={fieldData}
                allowAiTools={enableAiTools}
                aiOptions={enableAiTools && {
                    currentValue: fieldValue,
                    maxChars: fieldData.maxLength,
                    allowMaxCharsOverride: overrideCKValue.value,
                    onSetValue: (value) => {
                        setFieldValue(value);
                        editorRef.current[fieldId].setContent(value);
                    },
                    onSetLoading: setAiTextGenerating,
                }}
                allowSuggestedComments={fieldData.allowSuggestedComments && [0,2].includes(companyPrefs.suggestedCommentsMode)}
                allowVdp={false}
                charOverrideModalOptions={{
                    showCharCountModal: showCharCountModal,
                    modalMessage: modalMessage,
                    okCallback: okCallback,
                    cancelCallback: cancelCallback,
                    cancelButton: modalCancelButton,
                    enableLimitOverride: fieldData.enableLimitOverride,
                    onSetValue: (value) => {
                        setFieldValue(value);
                        editorRef.current[fieldId].setContent(value);
                    }
                }}
                charCount={replaceStr(fieldValue.replace(/\\[bfnrtvui]/g, '')).replace(/[\n]/g, '').length}// remove escape sequences when chars count FYI we are not removing html tags as per build2 functionality
                maxChars={fieldData.maxLength}
                maxCharsExceeded={replaceStr(fieldValue).length > fieldData.maxLength}
                allowOverride={fieldData.enableLimitOverride}
                overrideCKValue={overrideCKValue}
                overrideCheckHandler={overrideCheckHandler}
                tinyMceField={true}
            >
                <input
                    name={fieldData.fieldKey}
                    data-multipartfield="label"
                    value={fieldData.displayLabel}
                    hidden={true}
                    readOnly={true}
                />
                <textarea
                    name={fieldData.fieldKey}
                    data-multipartfield="data"
                    data-fieldname={fieldData.displayLabel}
                    value={fieldValue}
                    hidden={true}
                    readOnly={true}
                    xd-qa-type="text_field"
                    xd-qa-name={fieldData.fieldKey}
                />
                <div className={cx("editor-wrapper", {"includes-suggested-comments": !!(companyPrefs.suggestedCommentsMode && fieldData.allowSuggestedComments)})} xd-qa-type="card" xd-qa-name={fieldData.displayLabel + " Editor"}>
                    {aiTextGenerating && <div className="ai-text-loading-overlay"><ButtonSpinner size={50} spinnerColor="white"/></div>}
                    <Editor
                        apiKey="no-api-key"
                        id={fieldData.fieldKey}
                        name="mceEditor"
                        textareaName="mceEditor"
                        value={editorValue.value || "<p></p>"} // This controls the editor
                        onInit={(evt, editor) => {
                            editorRef.current[fieldId] = editor;

                            // Set initial content if needed
                            if (!isRefresh.value && fieldData.value && !fieldData.value.startsWith("<p>")) {
                            const formatted = fieldData.value.replaceAll(/(\r\n|\r|\n)/g, "<br>");
                            editor.setContent(formatted);
                            }
                        }}
                        init={{
                            statusbar: true,
                            menubar: false,
                            branding: true,
                            resize: false,
                            elementpath: false,
                            selection: true,
                            plugins: [
                            'charmap', 'searchreplace', 'wordcount'
                            ],
                            toolbar: ' charmap | bold italic underline | alignleft aligncenter alignright alignjustify | spellcheck | searchreplace | undo redo | pastetext',
                            setup: (editor) => {
                                editor.ui.registry.addButton('spellcheck', {
                                    icon: 'spell-check',
                                    onAction: () => {
                                        const newSpellCheck = !spellCheckEnabled.value;
                                         // Toggle the spellCheckEnabled state
                                         spellCheckEnabled.value = newSpellCheck;

                                        editor.iframeElement.contentDocument.body.spellcheck = newSpellCheck;

                                    }
                                });
                            },
                            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                        }}
                        tinymceScriptSrc="/webassets/tinymce6/tinymce.min.js"
                        onClick={(editor) => {
                            showFieldOptions.onChange(true);
                            showAiIcon.onChange(false);
                        }}
                        // try the following if need to use the old MCE version due to old plugin dependencies - this may work while also using the React bindings
                        // tinymceScriptSrc="/webassets/tinymce/jscripts/tiny_mce/tiny_mce.js"
                        onBeforeAddUndo={(evt, editor) => {
                            const charCount = editor.plugins.wordcount.body.getCharacterCount();
                            if (charCount > fieldData.maxLength && !overrideCKValue.value) {
                                evt.preventDefault();
                            }
                        }}
                        onFocus={(editor) => {
                            const uiFrame = window.document.getElementById(`field-options-ui-${fieldData.idPrimary.toString()}`);
                            const fieldOptionsTools = window.document.getElementById(`content-container-j43-${fieldData.idPrimary.toString()}`);
                            const simulatedBorder = window.document.getElementById(`simulated-border-${fieldData.idPrimary.toString()}`);
                            const childDiv = window.document.getElementById(`children-div-${fieldData.idPrimary.toString()}`);
                            const visibilityStyle = `
                                visibility: visible;
                                opacity: 1;
                                -webkit-transition: opacity 0.2s ease-in;
                                -moz-transition: opacity 0.2s ease-in;
                                -o-transition: opacity 0.2s ease-in;
                                display: flex;
                                z-index: 3;
                            `;
                            uiFrame.style.cssText = visibilityStyle;
                            fieldOptionsTools.style.cssText = `position: relative;`+visibilityStyle;
                            childDiv.style.zIndex = "4";
                            if (simulatedBorder) {
                                simulatedBorder.style.visibility= "visible";
                                simulatedBorder.style.opacity= "1";
                                simulatedBorder.style.display= "flex";
                                simulatedBorder.style.zIndex= "3";
                                simulatedBorder.style.transition= "opacity 0.2s ease-in";
                            }

                            showAiIcon.onChange(false);
                        }}
                        onBlur={() => {
                            const uiFrame = window.document.getElementById(`field-options-ui-${fieldData.idPrimary.toString()}`);
                            const fieldOptionsTools = window.document.getElementById(`content-container-j43-${fieldData.idPrimary.toString()}`);
                            const simulatedBorder = window.document.getElementById(`simulated-border-${fieldData.idPrimary.toString()}`);
                            const childDiv = window.document.getElementById(`children-div-${fieldData.idPrimary.toString()}`)
                            const visibilityStyle = `
                                visibility: hidden;
                                opacity: 0;
                                z-index: 1;
                            `;
                            uiFrame.style.cssText = visibilityStyle;
                            fieldOptionsTools.style.cssText = `position: absolute;`+visibilityStyle;
                            childDiv.style.zIndex = "1";
                            if (simulatedBorder) {
                                simulatedBorder.style.visibility = "hidden";
                                simulatedBorder.style.opacity = "0";
                            }

                            showAiIcon.onChange(true);
                        }}
                        onEditorChange={(newValue, editor) => {
                            let find = [
                                '<em>',
                                '</em>',
                                '<strong>',
                                '</strong>',
                                "&nbsp;",
                                '<span style="text-decoration: underline;">',
                                '</span>',
                                '&gt;',
                                '&lt;',
                                '|',
                                '\\i\\i',
                                '\\b\\b',
                                '\\u\\u',
                            ];

                            let replace = [
                                '\\i',
                                '\\i',
                                '\\b',
                                '\\b',
                                ' ',
                                '\\u',
                                '\\u',
                                '',
                                '',
                                '',
                                '',
                                '',
                                '',
                            ];

                            function findReplace(find, replace, string) {
                                for (let i = 0; i < find.length; i++) {
                                    string = string.replaceAll(find[i], replace[i]);
                                }
                                return string;
                            }

                            function removeLastCharsInsideTag(inputString, tagName, numCharsToRemove) {
                                const regex = new RegExp(`(<${tagName}>[^<]*)[^<]{${numCharsToRemove}}(<\/${tagName}>)`, 'i');
                                return inputString.replace(regex, '$1$2');
                            }

                            let newTextRTF = findReplace(find, replace, newValue);
                            let charCount = editor.plugins.wordcount.body.getCharacterCount();

                            if (charCount <= fieldData.maxLength || overrideCKValue.value) {
                                editorValue.onChange(newValue);
                                setFieldValue(newTextRTF);
                                if (charCount <= fieldData.maxLength) {
                                    editor.undoManager.add();
                                }
                            } else {
                                // Create a temporary element to parse the HTML
                                let tempElement = document.createElement('div');
                                tempElement.innerHTML = newValue;
                                // Extract the text content
                                let textContent = tempElement.textContent;
                                // Get the character count
                                let characterCount = textContent.length;
                                if (!fieldData.enableLimitOverride) {
                                    modalMessage.onChange('You are trying to paste/enter text with length more than allowed.');
                                    okCallback.onChange(() => {
                                        editorValue.onChange(removeLastCharsInsideTag(newValue, 'p', characterCount-fieldData.maxLength));
                                        showCharCountModal.onChange(false);
                                    });
                                    cancelCallback.onChange(() => {
                                        showCharCountModal.onChange(false)
                                    });
                                    modalCancelButton.onChange(false);
                                    return showCharCountModal.onChange(true);
                                } else if (!overrideCKValue.value) {
                                    modalMessage.onChange('You trying to paste/enter text with length more than allowed. Activate override?');
                                    okCallback.onChange(() => {
                                        overrideCKValue.onChange(true);
                                        editorValue.onChange(newValue);
                                        setFieldValue(newTextRTF);
                                        showCharCountModal.onChange(false);
                                    });
                                    cancelCallback.onChange(() =>{
                                        editorValue.onChange(removeLastCharsInsideTag(newValue, 'p', characterCount-fieldData.maxLength));
                                        showCharCountModal.onChange(false);
                                    });
                                    modalCancelButton.onChange(true);
                                    return showCharCountModal.onChange(true);
                                }
                            }
                        }}
                        ref={(ref) =>
                        {
                            const {iframeWindow, onBlur, onFocus} = tabGuardStatic.get();
                            const newIframeWindow = ref?.editor?.iframeElement?.contentWindow;

                            if (iframeWindow !== newIframeWindow) {
                                iframeWindow?.removeEventListener("blur", onBlur);
                                iframeWindow?.removeEventListener("focus", onFocus);
                                newIframeWindow?.addEventListener("blur", onBlur);
                                newIframeWindow?.addEventListener("focus", onFocus);
                                tabGuardStatic.set({iframeWindow: newIframeWindow, onBlur, onFocus});
                            }
                        }}
                    />
                    {(showAiIcon.value && !replaceStr(fieldValue).length) ?
                        <div className="sparkle-icon"
                            onClick={() => editorRef.current[fieldId]?.focus()}
                        />
                    : null}
                </div>
            </FieldOptions>
        </div>
    )
);
